import { createSlice } from '@reduxjs/toolkit'

// Helper functions for localStorage persistence
const PENDING_SUBSCRIPTIONS_KEY = 'stakingPendingSubscriptions'

const loadPendingSubscriptions = () => {
    try {
        const stored = localStorage.getItem(PENDING_SUBSCRIPTIONS_KEY)
        if (stored) {
            const parsed = JSON.parse(stored)
            // Filter out expired pending subscriptions (older than 10 minutes)
            const tenMinutesAgo = Date.now() - (10 * 60 * 1000)
            return parsed.filter(sub => new Date(sub.createdAt).getTime() > tenMinutesAgo)
        }
    } catch (error) {
        console.error('Error loading pending subscriptions from localStorage:', error)
    }
    return []
}

const savePendingSubscriptions = (subscriptions) => {
    try {
        localStorage.setItem(PENDING_SUBSCRIPTIONS_KEY, JSON.stringify(subscriptions))
    } catch (error) {
        console.error('Error saving pending subscriptions to localStorage:', error)
    }
}

const stakingSlice = createSlice({
    name: 'staking',
    initialState: {
        pools: [],
        loading: false,
        totalPools: 0,
        currentPage: 1,
        filters: {
            search: '',
            category: 'all',
            currency_id: null,
            duration: null
        },
        selectedPool: null,
        estimatedReward: null,
        apyData: {},
        tags: {
            loading: false,
            data: [],
            error: null
        },
        coins: {
            loading: false,
            data: [],
            error: null
        },
        portfolio: {
            loading: false,
            data: {
                total_value_usd: 0,
                total_earned_dollar: 0,
                total_earned_percentage_month: 0.0,
                average_apy: 0.0,
                active_staking: []
            }
        },
        pendingSubscriptions: loadPendingSubscriptions()
    },
    reducers: {
        setStakingPools: (state, action) => {
            // Handle both array and object payloads
            if (Array.isArray(action.payload)) {
                state.pools = action.payload
                state.totalPools = action.payload.length
                state.currentPage = 1
            } else {
                state.pools = action.payload.data
                state.totalPools = action.payload.total
                state.currentPage = action.payload.page
            }
        },
        appendStakingPools: (state, action) => {
            state.pools = [...state.pools, ...action.payload.data]
            state.totalPools = action.payload.total
            state.currentPage = action.payload.page
        },
        setStakingLoading: (state, action) => {
            state.loading = action.payload
        },
        setStakingFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload }
        },
        setSelectedPool: (state, action) => {
            state.selectedPool = action.payload
        },
        setEstimatedReward: (state, action) => {
            state.estimatedReward = action.payload
        },
        setAPYData: (state, action) => {
            const { currencyId, duration, apy } = action.payload
            const key = `${currencyId}_${duration}`
            state.apyData[key] = apy
        },
        setStakingTags: (state, action) => {
            state.tags.data = action.payload
            state.tags.loading = false
            state.tags.error = null
        },
        setStakingTagsLoading: (state, action) => {
            state.tags.loading = action.payload
        },
        setStakingTagsError: (state, action) => {
            state.tags.error = action.payload
            state.tags.loading = false
        },
        setStakingCoins: (state, action) => {
            state.coins.data = action.payload
            state.coins.loading = false
            state.coins.error = null
        },
        setStakingCoinsLoading: (state, action) => {
            state.coins.loading = action.payload
        },
        setStakingCoinsError: (state, action) => {
            state.coins.error = action.payload
            state.coins.loading = false
        },
        clearStakingData: (state) => {
            state.pools = []
            state.totalPools = 0
            state.currentPage = 1
            state.selectedPool = null
            state.estimatedReward = null
        },
        resetStakingFilters: (state) => {
            state.filters = {
                search: '',
                category: 'all',
                currency_id: null,
                duration: null
            }
        },
        setPortfolioLoading: (state, action) => {
            state.portfolio.loading = action.payload
        },
        setPortfolioData: (state, action) => {
            state.portfolio.data = action.payload
        },
        clearPortfolioData: (state) => {
            state.portfolio.data = {
                total_value_usd: 0,
                total_earned_dollar: 0,
                total_earned_percentage_month: 0.0,
                average_apy: 0.0,
                active_staking: []
            }
        },
        addPendingSubscription: (state, action) => {
            // Add a pending subscription to the list
            const pendingSubscription = {
                id: action.payload.id || `pending-${Date.now()}`,
                currency: action.payload.currency,
                asset: action.payload.asset,
                amount: action.payload.amount,
                apr: action.payload.apr,
                duration: action.payload.duration,
                durationDays: action.payload.durationDays,
                type: action.payload.type,
                status: 'Pending',
                createdAt: new Date().toISOString(),
                periodId: action.payload.periodId,
                iconUrl: action.payload.iconUrl,
                plan: action.payload.plan,
                rewards: action.payload.rewards,
                risk: action.payload.risk
            }

            state.pendingSubscriptions.push(pendingSubscription);
            // Save to localStorage
            savePendingSubscriptions(state.pendingSubscriptions);
        },
        removePendingSubscription: (state, action) => {
            // Remove a pending subscription by ID
            state.pendingSubscriptions = state.pendingSubscriptions.filter(
                sub => sub.id !== action.payload
            );
            // Save to localStorage
            savePendingSubscriptions(state.pendingSubscriptions);
        },
        clearPendingSubscriptions: (state) => {
            // Clear all pending subscriptions
            state.pendingSubscriptions = []
            // Save to localStorage
            savePendingSubscriptions(state.pendingSubscriptions);
        },
        updatePendingSubscriptionStatus: (state, action) => {
            // Update the status of a pending subscription
            const { id, status } = action.payload
            const subscription = state.pendingSubscriptions.find(sub => sub.id === id)
            if (subscription) {
                subscription.status = status
                // Save to localStorage
                savePendingSubscriptions(state.pendingSubscriptions);
            }
        },
        cleanupExpiredPendingSubscriptions: (state) => {
            // Remove expired pending subscriptions (older than 10 minutes)
            const tenMinutesAgo = Date.now() - (10 * 60 * 1000)
            const originalLength = state.pendingSubscriptions.length
            state.pendingSubscriptions = state.pendingSubscriptions.filter(sub =>
                new Date(sub.createdAt).getTime() > tenMinutesAgo
            )
            // Only save if we actually removed something
            if (state.pendingSubscriptions.length !== originalLength) {
                savePendingSubscriptions(state.pendingSubscriptions);
            }
        }
    }
})

export const {
    setStakingPools,
    appendStakingPools,
    setStakingLoading,
    setStakingFilters,
    setSelectedPool,
    setEstimatedReward,
    setAPYData,
    setStakingTags,
    setStakingTagsLoading,
    setStakingTagsError,
    setStakingCoins,
    setStakingCoinsLoading,
    setStakingCoinsError,
    clearStakingData,
    resetStakingFilters,
    setPortfolioLoading,
    setPortfolioData,
    clearPortfolioData,
    addPendingSubscription,
    removePendingSubscription,
    clearPendingSubscriptions,
    updatePendingSubscriptionStatus,
    cleanupExpiredPendingSubscriptions
} = stakingSlice.actions

export default stakingSlice.reducer
